import { useNavigate } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import {
  LEGAL_OPERATOR, LEGAL_CONTACT_EMAIL, LEGAL_JURISDICTION,
  LEGAL_LAST_UPDATED, LEGAL_EFFECTIVE_DATE, APP_NAME, APP_WEB_URL,
  SUBPROCESSORS,
} from "@/lib/legal";

// ─────────────────────────────────────────────────────────────────────
// Nova — Privacy Policy (launch-grade)
// Structure covers:
//   • Data controller identity        (all jurisdictions)
//   • Categories of data collected    (CCPA §1798.100)
//   • Purposes and legal bases        (GDPR Art. 6)
//   • Sub-processors                  (GDPR Art. 28)
//   • Data retention timelines
//   • Security posture
//   • User rights (US-CCPA, EU-GDPR, UK-DPA)
//   • Children (COPPA §312.4)
//   • International transfers         (SCCs)
//   • Cookies / local storage
//   • Do-Not-Track & Global Privacy Control
//   • Breach notification
//   • Contact + complaint routes
// ─────────────────────────────────────────────────────────────────────

function Section({ title, children, id }) {
  return (
    <section id={id} style={{ marginBottom: 24 }}>
      <h3 className="h3" style={{ color: "var(--text)", marginBottom: 10, fontSize: 16 }}>{title}</h3>
      <div className="body-sm" style={{ color: "var(--text-dim)", lineHeight: 1.7 }}>
        {children}
      </div>
    </section>
  );
}

const listStyle = { marginTop: 8, paddingLeft: 18 };

export default function PrivacyPolicyScreen() {
  const navigate = useNavigate();
  const mailto = `mailto:${LEGAL_CONTACT_EMAIL}?subject=${encodeURIComponent("Privacy request — Nova")}`;

  return (
    <div className="screen fade-in" data-testid="privacy-policy-screen" style={{ paddingBottom: 60 }}>
      <div className="top-bar">
        <button className="icon-btn" onClick={() => navigate(-1)} aria-label="Back" data-testid="privacy-back">
          <ChevronLeft size={18} />
        </button>
        <div className="logo-mark">PRIVACY</div>
        <span style={{ width: 32 }} />
      </div>

      <div className="eyebrow" style={{ marginTop: 16, marginBottom: 8 }}>
        LEGAL · EFFECTIVE {LEGAL_EFFECTIVE_DATE.toUpperCase()} · LAST UPDATED {LEGAL_LAST_UPDATED.toUpperCase()}
      </div>
      <h2 className="h1" style={{ fontSize: 30, marginBottom: 18, lineHeight: 1.12 }}>Privacy Policy</h2>

      <Section title="Summary (plain English)">
        <p>
          {APP_NAME} is an AI-powered fitness, nutrition, skincare, and style app. To give you
          personalised recommendations, we collect the profile details, meal scans, progress
          photos, and preferences <strong>you choose to submit</strong>. Photos are analysed by
          our AI providers and stored encrypted at rest. Payments go through Stripe — we never
          see your card number. You can delete your account and all associated data at any time
          from <em>Settings → Delete account</em>. We never sell your personal data.
        </p>
      </Section>

      <Section title="1. Who is responsible for your data">
        <p>
          <strong>Data controller / operator:</strong> {LEGAL_OPERATOR}, based in {LEGAL_JURISDICTION}.
          <br />
          <strong>Contact:</strong> <a href={mailto} style={{ color: "var(--gold)" }}>{LEGAL_CONTACT_EMAIL}</a>
          <br />
          <strong>App:</strong> {APP_NAME} ({APP_WEB_URL})
        </p>
      </Section>

      <Section title="2. Categories of personal data we collect">
        <p>We collect only what is needed to deliver the app you signed up for. The categories:</p>
        <ul className="bullet-list" style={listStyle}>
          <li><strong>Account identifiers:</strong> full name (optional), email address, bcrypt-hashed password. Assigned an internal UUID.</li>
          <li><strong>Profile data you enter:</strong> age, gender, height, weight, primary fitness goal, activity level, optional dietary preferences, optional allergies or skin sensitivities. All fields are optional beyond the minimum onboarding set.</li>
          <li><strong>Health & lifestyle inputs:</strong> meal scans, workouts logged, streak history, notes you write, coach conversations.</li>
          <li><strong>Photos:</strong> images you upload for nutrition scanning, skin analysis, hair coaching, outfit rating, or progress tracking. Encoded as base64 and stored in our encrypted-at-rest database. See Sections 4, 6, and 8 for how photos are handled.</li>
          <li><strong>Payment references:</strong> Stripe customer ID and subscription ID. We do <strong>not</strong> receive, see, or store your card number, CVC, or expiry — Stripe collects those directly on their PCI-DSS-compliant checkout page.</li>
          <li><strong>Device & connection metadata:</strong> IP address, browser/OS user agent, timezone, request timestamps, error logs, and diagnostic breadcrumbs. Used for security, rate-limiting, and debugging.</li>
          <li><strong>Push notification tokens:</strong> the opaque endpoint URL provided by APNs (iOS) or FCM (Android/web) when you opt in. Deleted when you opt out.</li>
          <li><strong>Cookies & local storage:</strong> a session token, your onboarding progress, and UI preferences. See Section 12.</li>
        </ul>
        <p style={{ marginTop: 8 }}>
          We do <strong>not</strong> collect precise geolocation, contacts, calendar, HealthKit,
          microphone, or biometric identifiers. If we ever add such features, they will be
          strictly opt-in and this policy will be updated first.
        </p>
      </Section>

      <Section title="3. Why we process each category (legal bases under GDPR)">
        <ul className="bullet-list" style={listStyle}>
          <li><strong>Contract</strong> — to create your account, run onboarding, compute your calorie/macro targets, deliver AI coaching, and process your subscription.</li>
          <li><strong>Legitimate interest</strong> — to keep the service secure (fraud/abuse detection, rate limits), maintain uptime, and improve the app based on aggregated, non-identifying usage patterns.</li>
          <li><strong>Consent</strong> — for push notifications, reminders, marketing emails (if any), and any processing that requires it under your local law.</li>
          <li><strong>Legal obligation</strong> — to retain payment records for tax purposes and respond to lawful government requests.</li>
        </ul>
        <p style={{ marginTop: 8 }}>
          You may withdraw consent at any time from Settings or by emailing us. Withdrawal
          does not affect the lawfulness of processing before withdrawal.
        </p>
      </Section>

      <Section title="4. Sub-processors we share data with">
        <p>We use the following vendors to run the service. Each has its own privacy policy and processes only the data needed for its role.</p>
        <div style={{ marginTop: 8, borderRadius: 10, overflow: "hidden", border: "1px solid var(--stroke, rgba(255,255,255,0.06))" }}>
          {SUBPROCESSORS.map((sp, i) => (
            <div key={sp.name} style={{
              padding: "10px 12px",
              background: i % 2 ? "rgba(255,255,255,0.02)" : "transparent",
              borderTop: i === 0 ? "none" : "1px solid var(--stroke, rgba(255,255,255,0.04))",
            }}>
              <div style={{ color: "var(--text)", fontWeight: 600, fontSize: 13 }}>{sp.name}</div>
              <div style={{ fontSize: 12, color: "var(--text-dim)", marginTop: 2 }}>{sp.purpose}</div>
              <div style={{ fontSize: 11, color: "var(--text-faint)", marginTop: 2 }}>Data hosted in {sp.country}</div>
            </div>
          ))}
        </div>
        <p style={{ marginTop: 10 }}>
          We send only the minimum data each sub-processor needs. For AI providers (OpenAI,
          Anthropic, Google), that means the photo bytes and text prompt for the specific
          scan you initiated — no account identifiers, no cross-request context. We instruct
          our AI providers not to train their models on our API traffic.
        </p>
      </Section>

      <Section title="5. How long we keep your data">
        <ul className="bullet-list" style={listStyle}>
          <li><strong>Active-account data</strong> (profile, scans, photos, streaks): retained while your account exists.</li>
          <li><strong>After you delete your account</strong>: profile, photos, scans, coach history, feed content, and preferences are permanently erased within 30 days. See Section 7 for the deletion mechanism.</li>
          <li><strong>Payment & tax records</strong>: retained by Stripe for the period required under US federal + state tax law (typically 7 years). Our own copy of the subscription reference is deleted with your account.</li>
          <li><strong>Aggregated / de-identified metrics</strong> (e.g. total active users, feature usage counts): retained indefinitely; contain no personal data by definition.</li>
          <li><strong>Server logs</strong>: 30 days rolling, then rotated out. Contains IP addresses and request paths for security investigation.</li>
        </ul>
      </Section>

      <Section title="6. Security">
        <ul className="bullet-list" style={listStyle}>
          <li><strong>Passwords</strong>: hashed with <code>bcrypt</code> (cost factor 12) — we never see or store your plaintext password.</li>
          <li><strong>Transport</strong>: all traffic between your device and our servers is encrypted with TLS 1.2+.</li>
          <li><strong>At rest</strong>: your database records (including photos) live in MongoDB Atlas with AES-256 encryption at rest.</li>
          <li><strong>Authentication</strong>: JWT bearer tokens with 7-day expiry, delivered via HttpOnly + Secure + SameSite=None cookies (so JavaScript on the page — malicious or otherwise — cannot read them).</li>
          <li><strong>Access controls</strong>: production database and secrets are gated to the operator only. No employees or contractors have routine access to your account.</li>
          <li><strong>Payments</strong>: PCI-DSS Level 1 handling delegated entirely to Stripe. Your card number never touches our servers.</li>
          <li><strong>Rate limiting & abuse protection</strong>: sensitive endpoints (login, register, password reset, checkout) are throttled per-IP and per-account.</li>
        </ul>
        <p style={{ marginTop: 8 }}>
          No system is perfectly secure. If you suspect unauthorised access to your account,
          email us immediately at <a href={mailto} style={{ color: "var(--gold)" }}>{LEGAL_CONTACT_EMAIL}</a>.
        </p>
      </Section>

      <Section title="7. Your rights and how to exercise them">
        <p>Regardless of where you live, you can:</p>
        <ul className="bullet-list" style={listStyle}>
          <li><strong>See & edit</strong> your profile data inside the app.</li>
          <li><strong>Delete</strong> your account and all associated data at any time via <em>Settings → Delete account</em>. This is instantaneous — we do not require a phone call or email confirmation.</li>
          <li><strong>Request a copy</strong> of your data by emailing us. We&apos;ll respond within 30 days with a machine-readable JSON export.</li>
          <li><strong>Correct</strong> anything inaccurate by editing your profile or emailing us.</li>
          <li><strong>Withdraw consent</strong> for notifications, reminders, or optional processing at any time from Settings.</li>
          <li><strong>Object</strong> to processing based on our legitimate interests. Email us and we will consider it.</li>
        </ul>
      </Section>

      <Section title="8. Photos — how they&apos;re handled">
        <p>Photos deserve extra clarity. When you upload a photo (a meal, your face for skin analysis, an outfit, a workout progress shot):</p>
        <ul className="bullet-list" style={listStyle}>
          <li>The photo is transmitted over TLS to our server, then forwarded to the AI provider needed for the specific feature (see Section 4).</li>
          <li>We instruct the provider not to train on our API traffic. Provider retention for API calls is typically 30 days for abuse-monitoring, then auto-purged.</li>
          <li>Our own copy of the photo is stored, encoded as base64 in our encrypted-at-rest database, under your account. Only you (via your authenticated session) can retrieve it.</li>
          <li>You can delete any individual photo from the app (scans, progress photos, saved outfits — each has a delete button), or wipe every photo along with your entire account.</li>
          <li>Photos of your face or body are never used for advertising, resold, or shared publicly without your explicit action (e.g. posting to the community feed).</li>
        </ul>
      </Section>

      <Section title="9. California residents (CCPA / CPRA)">
        <p>If you live in California, you have the following rights in addition to Section 7:</p>
        <ul className="bullet-list" style={listStyle}>
          <li><strong>Right to know</strong> the categories and specific pieces of personal information we have collected about you (categories are listed in Section 2).</li>
          <li><strong>Right to delete</strong> personal information (via Settings → Delete account, or by email).</li>
          <li><strong>Right to correct</strong> inaccurate information.</li>
          <li><strong>Right to opt out of &quot;sale&quot; or &quot;sharing&quot; of your personal information</strong>: we do not sell or share your personal information in the CCPA sense, so there is nothing to opt out of. If this ever changes, we will provide a &quot;Do Not Sell or Share My Personal Information&quot; link on our home page and give you 45 days&apos; notice.</li>
          <li><strong>Right to limit use of sensitive personal information</strong>: we do not use sensitive personal information (as defined in CPRA §1798.140(ae)) for purposes beyond delivering the service.</li>
          <li><strong>Right to non-discrimination</strong>: we will not deny service or charge different prices for exercising these rights.</li>
        </ul>
        <p style={{ marginTop: 8 }}>
          To exercise these rights, email <a href={mailto} style={{ color: "var(--gold)" }}>{LEGAL_CONTACT_EMAIL}</a>.
          We may verify your identity by asking you to log in and confirm the request from
          your account&apos;s registered email address.
        </p>
      </Section>

      <Section title="10. European Union / United Kingdom residents (GDPR / UK-DPA)">
        <p>If you live in the EU, EEA, UK, or Switzerland, you have the following rights under the GDPR (or the UK Data Protection Act 2018):</p>
        <ul className="bullet-list" style={listStyle}>
          <li>Right of access, rectification, erasure (&quot;right to be forgotten&quot;), restriction of processing, and data portability.</li>
          <li>Right to object to processing based on our legitimate interests, and to direct marketing.</li>
          <li>Right to withdraw consent at any time (see Section 3).</li>
          <li>Right to lodge a complaint with your local supervisory authority (e.g. the ICO in the UK, the CNIL in France, the Datenschutzbehörde in Austria).</li>
        </ul>
        <p style={{ marginTop: 8 }}>
          Because {LEGAL_OPERATOR} is based in the United States and does not have an
          EU-established entity, cross-border data transfers rely on Standard Contractual
          Clauses (Module 2, Commission Decision 2021/914) with our sub-processors, plus the
          supplementary measures described in Section 6.
        </p>
      </Section>

      <Section title="11. Children under 13 (COPPA) / under 16 (GDPR)">
        <p>
          {APP_NAME} is not directed at, and we do not knowingly collect personal information
          from, children under 13 (in the United States) or under 16 (in the EU/UK). Our
          onboarding requires you to affirm you meet the minimum age. If you believe a child
          has created an account, email us and we will delete it within 7 days.
        </p>
      </Section>

      <Section title="12. Cookies and local storage">
        <p>We use only strictly-necessary storage — no advertising cookies, no third-party trackers:</p>
        <ul className="bullet-list" style={listStyle}>
          <li><strong>Session cookie</strong> (<code>access_token</code>) — an HttpOnly, Secure, SameSite=None cookie holding your JWT. Cleared on logout. Duration: 7 days.</li>
          <li><strong>Local storage</strong> (<code>sr_token</code>) — a copy of your JWT for iOS PWA compatibility where cookies can behave unexpectedly. Cleared on logout.</li>
          <li><strong>Session storage</strong> — transient onboarding progress and error breadcrumbs. Cleared when you close the tab.</li>
          <li><strong>Push subscription</strong> — stored by your browser&apos;s push service; removed when you opt out from Settings.</li>
        </ul>
        <p style={{ marginTop: 8 }}>
          We respect the <em>Do Not Track</em> browser signal and Global Privacy Control (GPC): we do not track you for advertising in any case.
        </p>
      </Section>

      <Section title="13. International transfers">
        <p>
          Our servers, database, and AI sub-processors are located in the United States. If
          you use {APP_NAME} from outside the United States, your information will be
          transferred to, stored, and processed in the US. We rely on Standard Contractual
          Clauses and appropriate technical + organisational measures (Section 6) for cross-
          border transfers of personal data originating in the EU, EEA, UK, or Switzerland.
        </p>
      </Section>

      <Section title="14. Data breach notification">
        <p>
          If a breach of your personal information occurs that is likely to result in a risk
          to your rights and freedoms, we will notify you and the relevant supervisory
          authority without undue delay — within 72 hours where required under GDPR Art. 33,
          and as required under applicable US state breach-notification laws (e.g.
          Pennsylvania&apos;s 73 P.S. §§ 2301-2329, California&apos;s Civil Code §1798.82).
        </p>
      </Section>

      <Section title="15. Health & medical disclaimer">
        <p>
          {APP_NAME} is a wellness and lifestyle tool. It does <strong>not</strong> diagnose,
          treat, cure, or prevent any disease. AI-generated recommendations are informational
          only and are not a substitute for advice from a licensed physician, dermatologist,
          or registered dietitian. Always consult a qualified professional for medical
          questions — especially if you are pregnant, breastfeeding, under 18, or managing
          a chronic condition.
        </p>
      </Section>

      <Section title="16. Changes to this policy">
        <p>
          We will post material changes to this page and notify you via in-app banner and
          via email (to the address on your account) at least <strong>30 days</strong> before they
          take effect, unless the change is required by law to take effect sooner. Continued
          use of the service after the effective date constitutes acceptance.
        </p>
      </Section>

      <Section title="17. Contact and complaints">
        <p>
          Privacy questions, data requests, or complaints:&nbsp;
          <a href={mailto} style={{ color: "var(--gold)" }}>{LEGAL_CONTACT_EMAIL}</a>
        </p>
        <p style={{ marginTop: 6 }}>
          Operator: {LEGAL_OPERATOR}, {LEGAL_JURISDICTION}.
        </p>
        <p style={{ marginTop: 6 }}>
          If we cannot resolve your complaint to your satisfaction, EU/UK residents may lodge
          a complaint with their local data protection authority.
        </p>
      </Section>

      <div className="tiny" style={{ marginTop: 30, textAlign: "center", color: "var(--text-faint)", lineHeight: 1.55 }}>
        This policy is governed by the laws of {LEGAL_JURISDICTION}. Effective {LEGAL_EFFECTIVE_DATE}.
        Last updated {LEGAL_LAST_UPDATED}.
      </div>
    </div>
  );
}
